import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import {
  handleTabErrors,
  setConsentModalDetails,
} from "../../../../Redux/reducers/consentSlice";
import { ConsentLabelMap } from "../blockTypes";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Modal,
  SxProps,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { a11yProps, ModalTabPanel } from "../../../Common/UI/TabPanel";
import {
  ModalDrawerButtonStyles,
  ModalDrawerStyles,
  ModalHeaderTitleStyles,
} from "../../../Common/styles/modal";
import { BasicTab } from "./BasicTab";
import { saveConsent } from "../../../../Redux/actions/consentAction";
import { submitValidation } from "./utils";

export enum ValidationTypes {
  error = "error",
  warning = "warning",
  info = "info",
}

const ConsentPropertiesModal: React.FC = () => {
  const dispatch = useAppDispatch();

  const isLoading = useAppSelector(({ consent }) => consent.loading);
  const editable = useAppSelector(({ consent }) => consent.editable);
  const modalIsEdit = useAppSelector(({ consent }) => consent.modalIsEdit);

  const currentBlock = useAppSelector(({ consent }) => consent.modalConsent);
  const isBasicError = useAppSelector(({ consent }) => consent.isBasicError);

  const [tabIndex, setTabIndex] = useState("basic");

  let indicatorColor = "primary.main";
  if (tabIndex === "basic" && isBasicError) {
    indicatorColor = "error.main";
  }

  const closeModal = () => {
    dispatch(
      setConsentModalDetails({
        show: false,
        block: null,
        modalIsEdit: false,
      })
    );
    dispatch(
      handleTabErrors({
        errorHandler: {
          basic: "",
        },
      })
    );
  };

  const submitHandler = () => {
    const { errorHandler } = submitValidation(currentBlock);
    dispatch(handleTabErrors({ errorHandler: errorHandler }));

    if (errorHandler.ok) {
      dispatch(saveConsent(modalIsEdit));
    }
  };

  const changetabIndex = (_: any, oldValue: string) => {
    setTabIndex(oldValue);
  };

  const getTabSx = (isError: boolean): SxProps => ({
    textTransform: "capitalize",
    color: isError ? "error.main" : "primary.main",
    "&.Mui-selected": {
      color: isError ? "error.main" : "primary.main",
    },
  });

  return (
    <Modal open={true}>
      <Box sx={ModalDrawerStyles}>
        <Typography sx={ModalHeaderTitleStyles} mb={1}>
          {currentBlock ? ConsentLabelMap[currentBlock?.type] : "Block"}
        </Typography>
        <Box sx={{ borderBottom: 1, borderBottomColor: "#E5E7EB" }}>
          <Tabs
            value={tabIndex}
            onChange={changetabIndex}
            aria-label="Modal Settings"
            sx={{
              "& .MuiTabs-indicator": {
                backgroundColor: indicatorColor,
              },
            }}
          >
            <Tab
              label="Basic"
              value="basic"
              {...a11yProps(0)}
              sx={getTabSx(!!isBasicError)}
            />
          </Tabs>
        </Box>
        <ModalTabPanel value={tabIndex} index={"basic"}>
          <BasicTab isEdit={modalIsEdit} block={currentBlock} />
        </ModalTabPanel>

        <Box sx={{ mt: "auto" }}>
          <Divider />
          <Box sx={ModalDrawerButtonStyles}>
            <Button
              variant="outlined"
              onClick={closeModal}
              disabled={isLoading}
            >
              {editable ? "Cancel" : "Close"}
            </Button>
            {editable && (
              <Button
                variant="contained"
                onClick={submitHandler}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : modalIsEdit ? (
                  "Save"
                ) : (
                  "Add"
                )}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ConsentPropertiesModal;
